import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Box
} from '@mui/material';
import {
  Edit,
  ContentCopy,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { PromptEditor } from './PromptEditor';

interface LLMPrompt {
  promptKey: string;
  modelId: number;
  model?: {
    id: number;
    provider: string;
    modelName: string;
    alias?: string;
    family: string;
    release: string;
  };
  providerType?: string;
  systemPrompt?: string;
  prompt: string;
  temperature: number;
  maxTokens: number;
  pdfCharacters: number;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface PromptsManagementProps {
  prompts: LLMPrompt[];
  onEditPrompt: (prompt: LLMPrompt) => void;
  isEditorOpen: boolean;
  selectedPrompt: LLMPrompt | null;
  isCreating: boolean;
  onCloseEditor: () => void;
}

export const PromptsManagement: React.FC<PromptsManagementProps> = ({
  prompts,
  onEditPrompt,
  isEditorOpen,
  selectedPrompt,
  isCreating,
  onCloseEditor
}) => {
  const getPromptCategory = (promptKey: string): string => {
    if (promptKey.includes('description')) return 'Descriptions';
    if (promptKey.includes('finding')) return 'Key Findings';
    if (promptKey.includes('podcast')) return 'Podcast';
    if (promptKey.includes('system')) return 'System';
    return 'Other';
  };

  const getCategoryColor = (category: string): 'primary' | 'secondary' | 'success' | 'warning' | 'default' => {
    switch (category) {
      case 'Descriptions': return 'primary';
      case 'Key Findings': return 'secondary';
      case 'Podcast': return 'success';
      case 'System': return 'warning';
      default: return 'default';
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Prompt Key</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.map((prompt) => {
              const category = getPromptCategory(prompt.promptKey);
              return (
                <TableRow key={prompt.promptKey} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {prompt.promptKey}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={category}
                      size="small"
                      color={getCategoryColor(category)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {prompt.model?.provider || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {prompt.model?.modelName || 'No model'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {prompt.isActive ? (
                      <Chip
                        icon={<Visibility />}
                        label="Active"
                        size="small"
                        color="success"
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        icon={<VisibilityOff />}
                        label="Inactive"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Edit Prompt">
                        <IconButton
                          size="small"
                          onClick={() => onEditPrompt(prompt)}
                          color="primary"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clone Prompt">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // TODO: Implement clone functionality
                            console.log('Clone prompt:', prompt.promptKey);
                          }}
                          color="secondary"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Prompt Editor Dialog */}
      {isEditorOpen && (
        <PromptEditor
          open={isEditorOpen}
          onClose={onCloseEditor}
          prompt={selectedPrompt}
          isCreating={isCreating}
        />
      )}
    </>
  );
};